<template>
    <PanelPage>
        <template v-slot:title>
            <SmartBreadcrumbs
                type="InertiaLink"
                :pages="[
                    {
                        name: $tc('messages.activities', 2),
                        href: meta.links.activities_index,
                    },
                    {
                        name: $t('messages.practice'),
                        href: null,
                    },
                ]"
            />
        </template>

        <PracticeModeInfo :practice-mode="meta.info.practice_mode" />

        <div v-if="meta.links.preview_modes.length && !meta.info.duelPartner">
            <div>
                {{ $t('messages.preview_levels') }}
            </div>
            <NavLinks :links="meta.links.preview_modes" />
        </div>
        <div v-if="meta.links.exit_preview_mode" class="mt-3">
            <AppButton element="InertiaLink" :href="meta.links.exit_preview_mode" desired-action>
                {{ $t('messages.return_to_practice') }}
            </AppButton>
        </div>

        <div>
            <div>
                {{ $tc('messages.grade_levels', 1) }}
            </div>
            <NavLinks :links="meta.nav_links.grade_levels" />
        </div>

        <div class="w-full flex space-x-4 justify-end">
            <AppButton element="InertiaLink" :href="meta.links.tag_attempts_index" v-if="!meta.info.duelPartner">
                {{ $t('messages.results_per_tag') }}
            </AppButton>
            <AppButton element="InertiaLink" :href="meta.links.tag_scores_index" v-if="!meta.info.duelPartner">
                {{ $t('messages.tag_scores') }}
            </AppButton>
            <AppButton element="InertiaLink" :href="meta.links.practice_duels_index">
                {{ $tc('messages.duels', 2) }}
            </AppButton>
        </div>

        <div v-if="meta.info.duelPartner">
            <div class="flex space-x-2 items-center">
                <img
                    class="relative z-30 inline-block h-10 w-10 rounded-full ring-2 ring-white"
                    :src="meta.info.duelPartner.avatar_or_default"
                    alt="profile photo"
                />
                <div>
                    {{
                        $tc('messages.choose_tag_for_duel_with_X', 10, { name: meta.info.duelPartner.name_or_nickname })
                    }}
                </div>
                <div>
                    <AppButton element="InertiaLink" :href="meta.links.index_cancel_duel">
                        {{ $t('messages.cancel') }}
                    </AppButton>
                </div>
            </div>
        </div>

        <EmptyState v-if="subjects.length === 0" picture />
        <div v-else class="flex flex-wrap gap-x-2 mt-3">
            <AppButton
                element="InertiaLink"
                :href="subject.url"
                v-for="subject in subjects"
                :key="subject.id"
                class="flex gap-x-2"
            >
                {{ subject.text }}

                <IconCircleCheckFilled class="h-5 w-5 text-indigo-600" v-if="filters.subject_id == subject.id" />
            </AppButton>
        </div>

        <div v-if="!!form.subject_id" class="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-12">
            <div class="sm:col-span-3">
                <FormLabel :value="$t('messages.difficulty')" for="difficulty" />
                <div class="flex gap-4">
                    <InertiaLink
                        v-for="link in meta.links.difficulties"
                        :key="link.text"
                        :href="link.url"
                        class="rounded-full flex items-center justify-center cursor-pointer focus:outline-none ring-indigo-500"
                    >
                        <span
                            aria-hidden="true"
                            class="w-6 h-6 border border-black border-opacity-10 rounded-full tabular-nums text-xs flex justify-center align-middle items-center"
                            :class="{
                                'bg-red-400 text-red-800 border-red-800 border-2':
                                    link.text == filters.difficulty && link.text == 1,
                                'bg-orange-400 text-orange-800 border-orange-800 border-2':
                                    link.text == filters.difficulty && link.text == 2,
                                'bg-yellow-400 text-yellow-800 border-yellow-800 border-2':
                                    link.text == filters.difficulty && link.text == 3,
                                'bg-blue-400 text-blue-800 border-blue-800 border-2':
                                    link.text == filters.difficulty && link.text == 4,
                                'bg-green-400 text-green-800 border-green-800 border-2':
                                    link.text == filters.difficulty && link.text == 5,
                                'bg-gray-200 text-gray-400': link.text != filters.difficulty,
                            }"
                        >
                            {{ link.text }}
                        </span>
                    </InertiaLink>
                </div>
                <FormError :value="form.errors.difficulty" class="mt-2" />
            </div>
            <div class="sm:col-span-3">
                <FormLabel :value="$t('messages.questions_quantity')" for="questions_quantity">
                    <HelpTooltip>
                        <div class="whitespace-pre-line">
                            {{ $t('messages.practice_questions_quantity_explanation') }}
                        </div>
                    </HelpTooltip>
                </FormLabel>
                <div class="flex gap-4">
                    <InertiaLink
                        v-for="link in meta.links.quantities"
                        :key="link.text"
                        :href="link.url"
                        class="rounded-full flex items-center justify-center cursor-pointer focus:outline-none ring-indigo-500"
                    >
                        <span
                            aria-hidden="true"
                            class="w-6 h-6 border border-black border-opacity-10 rounded-full tabular-nums text-xs flex justify-center align-middle items-center"
                            :class="{
                                'bg-indigo-400 text-white border-red-800 border-2': link.text == filters.qty,
                                'bg-gray-200 text-gray-600': link.text != filters.qty,
                            }"
                        >
                            &times;{{ link.text }}
                        </span>
                    </InertiaLink>
                </div>
                <FormError :value="form.errors.qty" class="mt-2" />
            </div>
            <div v-if="contentBlocks.length" class="mt-5 sm:col-span-12">
                <div class="mt-1" v-if="meta.info.practice_mode.is_active">
                    <FormLabel :value="$t('messages.show_all')" for="show_all" />
                    <input
                        v-model="showAll"
                        id="show_all"
                        type="checkbox"
                        class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                    />
                </div>

                <AlertMessage class="mt-3" v-if="meta.info.race_info" type="info">
                    <template v-slot:icon>
                        <IconSpeedboat class="text-indigo-500 h-6 w-6" />
                    </template>
                    <template v-slot:body>
                        <div class="flex gap-2">
                            <div>
                                <div>
                                    {{ meta.info.race_info.message }}
                                </div>
                                <div class="text-sm">
                                    {{ meta.info.race_info.tags_info }}
                                </div>
                            </div>
                            <div>
                                <AppButton element="InertiaLink" :href="meta.info.race_info.url" desired-action>
                                    {{ $t('messages.view_progress') }}
                                </AppButton>
                            </div>
                        </div>
                    </template>
                </AlertMessage>

                <AppWell class="mt-5">
                    <div class="md:grid md:grid-cols-2 lg:grid-cols-3 md:gap-6 mt-3">
                        <div v-for="block in filteredContentBlocks" :key="block.id">
                            <AppCard>
                                <div class="p-1">
                                    <div class="font-bold">
                                        {{ block.name }}
                                    </div>
                                    <div class="pl-3 divide-y divide-gray-300">
                                        <div class="mt-3" v-for="section in block.sections" :key="section.name">
                                            <div class="mt-3">
                                                {{ section.name }}
                                            </div>
                                            <div class="pl-3">
                                                <div
                                                    v-for="tag in section.tags"
                                                    :key="tag.id"
                                                    class="mt-1 flex items-center gap-x-2"
                                                >
                                                    <div>
                                                        <MedallionLevelsInfo :levels-info="tag.levels_info" />
                                                    </div>
                                                    <AppButton
                                                        element="button"
                                                        type="button"
                                                        v-if="tag.url === null"
                                                        :title="tag.description"
                                                        disabled
                                                    >
                                                        {{ tag.name }}
                                                    </AppButton>
                                                    <div v-else-if="tag.blocked_reason" class="flex gap-2 items-center">
                                                        <AppButton
                                                            element="button"
                                                            type="button"
                                                            :title="tag.description"
                                                            disabled
                                                        >
                                                            {{ tag.name }}
                                                        </AppButton>

                                                        <Tippy>
                                                            <button>
                                                                <IconExclamationCircleFilled
                                                                    class="h-6 w-6 text-yellow-500"
                                                                />
                                                            </button>
                                                            <template #content>
                                                                {{ tag.blocked_reason }}
                                                            </template>
                                                        </Tippy>
                                                    </div>
                                                    <AppButton
                                                        v-else
                                                        element="InertiaLink"
                                                        :href="tag.url"
                                                        :title="tag.description"
                                                        :desired-action="tag.desired_action"
                                                    >
                                                        {{ tag.name }}
                                                    </AppButton>
                                                    <div>
                                                        <TagHelpSlideOver :tag="tag" />
                                                    </div>
                                                    <div v-if="tag.expected_level">
                                                        <Tippy>
                                                            <button>
                                                                <BadgePill color="indigo">
                                                                    {{ tag.expected_level }}
                                                                </BadgePill>
                                                            </button>
                                                            <template #content>
                                                                <span>
                                                                    {{
                                                                        $t('messages.expected_level_X_indicator', {
                                                                            level: tag.expected_level,
                                                                        })
                                                                    }}
                                                                </span>
                                                            </template>
                                                        </Tippy>
                                                    </div>
                                                    <IconSpeedboat class="text-indigo-500 h-5 w-5" v-if="tag.in_race" />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </AppCard>
                        </div>
                    </div>
                </AppWell>
            </div>
            <EmptyState v-else-if="!!filters.difficulty" picture />
        </div>
    </PanelPage>
</template>

<script>
import Layout from '@/Shared/Layout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import EmptyState from '@/components/EmptyState.vue';
import AppButton from '@/components/AppButton.vue';
import FormLabel from '@/components/Forms/FormLabel.vue';
import FormError from '@/components/Forms/FormError.vue';
import { IconCircleCheckFilled, IconExclamationCircleFilled, IconSpeedboat } from '@tabler/icons-vue';
import AppCard from '@/Shared/AppCard.vue';
import AppWell from '@/Shared/AppWell.vue';
import AlertMessage from '@/components/AlertMessage.vue';
import MedallionLevelsInfo from '@/components/MedallionLevelsInfo.vue';
import NavLinks from '@/components/NavLinks.vue';
import TagHelpSlideOver from '@/Pages/Student/Activity/Practice/partials/TagHelpSlideOver.vue';
import { Tippy } from 'vue-tippy';
import BadgePill from '@/components/BadgePill.vue';
import HelpTooltip from '../../../../components/HelpTooltip.vue';
import PracticeModeInfo from '../../../../components/PracticeModeInfo.vue';

export default {
    components: {
        PracticeModeInfo,
        HelpTooltip,
        BadgePill,
        Tippy,
        TagHelpSlideOver,
        NavLinks,
        MedallionLevelsInfo,
        AlertMessage,
        AppWell,
        AppCard,
        FormError,
        FormLabel,
        AppButton,
        PanelPage,
        EmptyState,
        IconCircleCheckFilled,
        IconSpeedboat,
        IconExclamationCircleFilled,
    },
    layout: Layout,
    name: 'Index',
    props: ['subjects', 'contentBlocks', 'filters', 'meta'],
    data() {
        return {
            form: this.$inertia.form({
                subject_id: this.filters.subject_id,
                difficulty: this.filters.difficulty,
            }),
            showAll: false,
        };
    },
    computed: {
        filteredContentBlocks() {
            return this.contentBlocks
                .map((block) => {
                    if (this.showAll === true || this.meta.info.practice_mode.is_active === false) {
                        return block;
                    }
                    return {
                        ...block,
                        sections: block.sections
                            .map((section) => {
                                return {
                                    ...section,
                                    tags: section.tags.filter((tag) => {
                                        return tag.desired_action;
                                    }),
                                };
                            })
                            .filter((s) => s.tags.length),
                    };
                })
                .filter((cb) => cb.sections.length);
        },
    },
};
</script>
